import React, { useEffect, useRef, useState } from "react";
import { Box, Camera, FileUp } from "lucide-react";
import { presentationApi } from "../../../services/presentationApi";
import { focusApi } from "../../../services/focusApi";

const input = "w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none";
const MAX_BYTES = 6_000_000;

// Choix du jumeau d'un bloc ou d'une zone visuelle : page HTML envoyée, jumeau déjà ajouté, ou diapo d'un Focus
// tech (celui présenté en tactique). Renvoie la référence « embed:ID ». L'image fixe est capturée par le serveur.
const TwinPicker = ({ value, onChange }) => {
  const fileRef = useRef(null);
  const [embeds, setEmbeds] = useState([]);
  const [focuses, setFocuses] = useState([]);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    presentationApi.embeds({ limit: 50 }).then((result) => setEmbeds(result.items || [])).catch(() => {});
    focusApi.list("all").then((result) => setFocuses((result.items || result || []).filter((item) => item.slide_count)))
      .catch(() => {});
  }, []);

  const run = async (label, action) => {
    setBusy(label);
    setError("");
    try {
      const embed = await action();
      setEmbeds((current) => [embed, ...current.filter((item) => item.id !== embed.id)]);
      onChange(embed.ref);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  };

  const upload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > MAX_BYTES) { setError("Page trop lourde (6 Mo au plus)."); return; }
    run("Envoi et capture de l'image fixe…", async () => presentationApi.uploadEmbed({ filename: file.name, html: await file.text() }));
  };

  const current = embeds.find((item) => item.ref === value);
  return (
    <div className="space-y-2">
      <select className={input} value={value || ""} onChange={(event) => onChange(event.target.value || null)}>
        <option value="">Aucun jumeau</option>
        {value && !current && <option value={value}>{value}</option>}
        {embeds.map((item) => <option key={item.id} value={item.ref}>{item.filename || item.ref}</option>)}
      </select>
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={Boolean(busy)} onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50">
          <FileUp className="h-3.5 w-3.5" /> Page HTML
        </button>
        {value && (
          <button type="button" disabled={Boolean(busy)} title="Capturer à nouveau l'image fixe (PDF, miniatures)"
            onClick={() => run("Capture…", () => presentationApi.refreshPoster(value.slice(6)))}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50">
            <Camera className="h-3.5 w-3.5" /> Recapturer
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept=".html,.htm,text/html" className="hidden" onChange={upload} aria-label="Page HTML du jumeau" />
      {focuses.length > 0 && (
        <select className={input} value="" disabled={Boolean(busy)}
          onChange={(event) => event.target.value && run("Reprise du Focus tech…",
            () => presentationApi.embedFromFocus({ focus_id: Number(event.target.value) }))}>
          <option value="">Reprendre un Focus tech…</option>
          {focuses.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
        </select>
      )}
      {busy && <p className="inline-flex items-center gap-1 text-xs text-slate-500"><Box className="h-3 w-3 animate-pulse" /> {busy}</p>}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default TwinPicker;
